import type {
    WmlExportFormat,
    CoreExportFormat,
    ExportFormat,
    RawComponentExport,
    ReferencesMap,
    ImportExportConverter,
    ImportExportFormatConverter
} from '@wix/document-services-types'
import {ReportableError} from '@wix/document-manager-utils'
import {importPackage} from '@wix/document-manager-dynamic-import'
import * as wmlConverterModule from '@wix/document-management-wml-converter'
import * as emlConverterModule from '@wix/document-management-eml-converter'
import * as paranativeConverterModule from '@wix/document-management-paranative-converter'
import * as coreConverterModule from '@wix/document-management-core-converter'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {CoreLogger} from '@wix/document-manager-core'
import type {CssFile} from '@wix/document-management-paranative-converter'

type FormatConverterType = 'wml'
type ConverterType = FormatConverterType | 'core'
type ConvertersMap = {
    [K in ConverterType]: {
        converterName: string
        converterModule: ImportExportConverter
    }
}

const convertersMap: ConvertersMap = {
    core: {
        converterName: '@wix/document-management-core-converter',
        converterModule: coreConverterModule
    },
    wml: {
        converterName: '@wix/document-management-wml-converter',
        converterModule: wmlConverterModule
    }
}

const getDmConverterSourceQuery = (): string | undefined => {
    try {
        return new URLSearchParams(window.location.search).get('DmConverterSource') ?? undefined
    } catch (e) {
        /* not browser */
    }
}

const loadConverter = async <T extends ImportExportConverter>(
    nameWithVersion: string,
    logger?: CoreLogger
): Promise<T> => {
    try {
        logger?.interactionStarted('load-converter', {nameWithVersion})
        const result = await importPackage<T>(nameWithVersion)
        logger?.interactionEnded('load-converter', {nameWithVersion, success: true})
        return result
    } catch (error: any) {
        logger?.interactionEnded('load-converter', {nameWithVersion, success: false})
        logger?.captureError(error, {
            tags: {feature: 'import-export-loader'},
            extras: {nameWithVersion}
        })

        throw new ReportableError({
            errorType: 'cannotGetConverterPackage',
            message: `could not get document management converter package: ${nameWithVersion}`,
            extras: {nameWithVersion, error: error?.message ?? error?.code}
        })
    }
}

const getConverterModule = async <T extends ImportExportConverter>(
    componentExport: ExportFormat,
    converterType: ConverterType,
    logger?: CoreLogger
): Promise<T> => {
    const {version} = componentExport
    if (!version) {
        throw new ReportableError({
            errorType: 'missingImportExportVersion',
            message: 'ImportExport version is missing'
        })
    }

    const converterSourceQuery = getDmConverterSourceQuery()

    if (converterSourceQuery && !converterSourceQuery.startsWith('http://localhost:')) {
        throw new ReportableError({
            errorType: 'INVALID_DM_CONVERTER_SOURCE',
            message: `DM converter source must be a localhost url`,
            extras: {converterSourceQuery}
        })
    }

    const {converterModule, converterName} = convertersMap[converterType]
    if (!converterSourceQuery && version === converterModule.converter.getVersion()) {
        return converterModule as T
    }

    if (converterSourceQuery) {
        // TODO: Localhost/custom source loading not supported by new loader
        throw new ReportableError({
            errorType: 'INVALID_DM_CONVERTER_SOURCE',
            message: `Custom converter source is not supported in this version`,
            extras: {converterSourceQuery}
        })
    }

    return loadConverter(`${converterName}@${version}`, logger)
}

const convertComponentExportToCoreExport = async (
    componentExport: ExportFormat,
    converterType: FormatConverterType,
    logger?: CoreLogger
): Promise<CoreExportFormat> => {
    const {converter} = await getConverterModule<ImportExportFormatConverter>(componentExport, converterType, logger)
    return converter.unpack(deepClone(componentExport))
}

export const convertCoreToRawComponentExport = async (
    componentExport: CoreExportFormat,
    logger?: CoreLogger
): Promise<RawComponentExport> => {
    const {converter} = await getConverterModule<ImportExportConverter<CoreExportFormat, RawComponentExport>>(
        componentExport,
        'core',
        logger
    )
    return converter.unpack(deepClone(componentExport))
}

export const convertRawToCoreComponentExport = (componentExport: RawComponentExport): CoreExportFormat =>
    coreConverterModule.converter.pack(componentExport)

const convertWmlToCoreComponentExport = async (
    componentExport: WmlExportFormat,
    logger?: CoreLogger
): Promise<CoreExportFormat> => convertComponentExportToCoreExport(componentExport, 'wml', logger)

export const convertWmlToRawComponentExport = async (
    componentExport: WmlExportFormat,
    logger?: CoreLogger
): Promise<RawComponentExport> => {
    const coreExport = await convertWmlToCoreComponentExport(componentExport, logger)
    return convertCoreToRawComponentExport(coreExport, logger)
}

export const convertRawToWmlComponentExport = (componentExport: RawComponentExport): WmlExportFormat =>
    wmlConverterModule.converter.pack(convertRawToCoreComponentExport(componentExport))

export const convertCoreToWmlComponentExport = (componentExport: CoreExportFormat): WmlExportFormat =>
    wmlConverterModule.converter.pack(componentExport)

export const convertRawToEmlComponentExport = (
    componentExport: RawComponentExport,
    context?: emlConverterModule.EmlExportContext
): emlConverterModule.EmlExportFormat =>
    emlConverterModule.converter.pack(convertRawToCoreComponentExport(componentExport), context)

export const convertRawToParanativeComponentExport = (
    componentExport: RawComponentExport
): paranativeConverterModule.ParanativeExportFormat =>
    paranativeConverterModule.converter.pack(convertRawToCoreComponentExport(componentExport))

export const convertRawToParanativeGlobalStylesExport = (
    componentExport: RawComponentExport,
    references?: ReferencesMap
): CssFile =>
    paranativeConverterModule.converter.generateGlobalStyles(
        convertRawToCoreComponentExport(componentExport),
        references
    )
